// Holiday Mode Switch Component for Master Admin
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, PartyPopper, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HolidayModeCard({ getAuthHeaders }) {
  const [holidayMode, setHolidayMode] = useState({
    enabled: false,
    start_date: null,
    end_date: null,
    message: ''
  });
  const [dateRange, setDateRange] = useState({
    from: null,
    to: null
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchHolidayMode();
  }, []);

  const fetchHolidayMode = async () => {
    try {
      const response = await axios.get(`${API}/settings/holiday-mode`, getAuthHeaders());
      setHolidayMode(response.data);
      
      // Set date range from response
      if (response.data.start_date || response.data.end_date) {
        setDateRange({
          from: response.data.start_date ? new Date(response.data.start_date + 'T12:00:00') : null,
          to: response.data.end_date ? new Date(response.data.end_date + 'T12:00:00') : null
        });
      }
    } catch (error) {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (enabled) => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings/holiday-mode`, {
        enabled,
        start_date: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
        end_date: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
        message: holidayMode.message || 'Lo sentimos, estamos cerrados por días festivos.'
      }, getAuthHeaders());
      
      setHolidayMode(response.data);
      toast.success(enabled ? 'Modo festivo activado' : 'Modo festivo desactivado');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar modo festivo');
    } finally {
      setSaving(false);
    }
  };

  const handleDateSelect = (range) => {
    setDateRange({
      from: range?.from || null,
      to: range?.to || null
    });
    
    if (range?.to) {
      setCalendarOpen(false);
    }
  };

  const handleSaveDates = async () => {
    setSaving(true);
    try {
      const response = await axios.put(`${API}/settings/holiday-mode`, {
        enabled: holidayMode.enabled,
        start_date: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : null,
        end_date: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : null,
        message: holidayMode.message
      }, getAuthHeaders());
      
      setHolidayMode(response.data);
      toast.success('Fechas actualizadas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar fechas');
    } finally {
      setSaving(false);
    }
  };

  const formatDateRange = () => {
    if (!dateRange.from && !dateRange.to) return 'Seleccionar fechas';
    if (!dateRange.to) return format(dateRange.from, 'dd MMM yyyy', { locale: es });
    return `${format(dateRange.from, 'dd MMM', { locale: es })} - ${format(dateRange.to, 'dd MMM yyyy', { locale: es })}`;
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800 p-6">
        <div className="animate-pulse h-20 bg-zinc-800 rounded"></div>
      </Card>
    );
  }

  return (
    <Card className={`border p-6 transition-colors ${
      holidayMode.enabled 
        ? 'bg-red-500/10 border-red-500/30' 
        : 'bg-zinc-900/50 border-zinc-800'
    }`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-lg ${
            holidayMode.enabled ? 'bg-red-500/20' : 'bg-amber-500/20'
          }`}>
            <PartyPopper className={`w-6 h-6 ${
              holidayMode.enabled ? 'text-red-500' : 'text-amber-500'
            }`} />
          </div>
          <div>
            <h3 className="text-white font-medium flex items-center gap-2">
              Día Festivo
              {holidayMode.enabled && (
                <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded-full">
                  ACTIVO
                </span>
              )}
            </h3>
            <p className="text-sm text-zinc-500">
              Bloquea el agendador de citas
            </p>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <Label htmlFor="holiday-mode" className="text-zinc-400 text-sm">
            {holidayMode.enabled ? 'Cerrado' : 'Abierto'}
          </Label>
          <Switch
            id="holiday-mode"
            checked={holidayMode.enabled}
            onCheckedChange={handleToggle}
            disabled={saving}
            className="data-[state=checked]:bg-red-500"
            data-testid="holiday-mode-switch"
          />
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="mt-4 pt-4 border-t border-zinc-800">
        <Label className="text-zinc-400 text-sm mb-2 block">
          Período de cierre
        </Label>
        <div className="flex gap-2">
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`flex-1 justify-start text-left font-normal ${
                  holidayMode.enabled 
                    ? 'bg-red-500/10 border-red-500/30 text-white hover:bg-red-500/20' 
                    : 'bg-zinc-800 border-zinc-700 text-white hover:bg-zinc-700'
                }`}
                data-testid="holiday-date-picker"
              >
                <CalendarDays className="mr-2 h-4 w-4 text-zinc-400" />
                {formatDateRange()}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
              {/* Quick presets */}
              <div className="flex gap-2 p-3 border-b border-zinc-800">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    setDateRange({ from: today, to: today });
                  }}
                  className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  Hoy
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    setDateRange({ from: today, to: addDays(today, 1) });
                  }}
                  className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  2 días
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const today = new Date();
                    setDateRange({ from: today, to: addDays(today, 6) });
                  }}
                  className="text-xs text-zinc-400 hover:text-white hover:bg-zinc-800"
                >
                  1 semana
                </Button>
              </div>
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange.from || new Date()}
                selected={dateRange}
                onSelect={handleDateSelect}
                numberOfMonths={2}
                locale={es}
                className="text-white"
                classNames={{
                  months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                  month: "space-y-4",
                  caption: "flex justify-center pt-1 relative items-center text-white",
                  caption_label: "text-sm font-medium text-white",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 text-white border border-zinc-700 rounded-sm hover:bg-zinc-800",
                  nav_button_previous: "absolute left-1",
                  nav_button_next: "absolute right-1",
                  table: "w-full border-collapse space-y-1",
                  head_row: "flex",
                  head_cell: "text-zinc-500 rounded-md w-8 font-normal text-[0.8rem]",
                  row: "flex w-full mt-2",
                  cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-red-500/20 [&:has([aria-selected].day-outside)]:bg-red-500/10 [&:has([aria-selected].day-range-end)]:rounded-r-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md",
                  day: "h-8 w-8 p-0 font-normal aria-selected:opacity-100 text-white hover:bg-zinc-800 rounded-sm",
                  day_range_start: "day-range-start bg-red-500 text-white hover:bg-red-500 hover:text-white",
                  day_range_end: "day-range-end bg-red-500 text-white hover:bg-red-500 hover:text-white",
                  day_selected: "bg-red-500 text-white hover:bg-red-500 hover:text-white focus:bg-red-500 focus:text-white",
                  day_today: "bg-zinc-800 text-amber-500",
                  day_outside: "day-outside text-zinc-600 aria-selected:bg-red-500/20 aria-selected:text-zinc-400",
                  day_disabled: "text-zinc-700 opacity-50",
                  day_range_middle: "aria-selected:bg-red-500/20 aria-selected:text-white",
                  day_hidden: "invisible",
                }}
              />
            </PopoverContent>
          </Popover>
          
          <Button
            onClick={handleSaveDates}
            disabled={saving || (!dateRange.from && !dateRange.to)}
            className="bg-zinc-700 hover:bg-zinc-600 text-white"
            size="icon"
            title="Guardar fechas"
          >
            <CalendarDays className="w-4 h-4" />
          </Button>
        </div>
        
        {dateRange.from && dateRange.to && (
          <p className="text-xs text-zinc-500 mt-2">
            {holidayMode.enabled ? (
              <span className="text-red-400">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                El agendador estará bloqueado del {format(dateRange.from, "d 'de' MMMM", { locale: es })} al {format(dateRange.to, "d 'de' MMMM yyyy", { locale: es })}
              </span>
            ) : (
              `Al activar, se bloqueará del ${format(dateRange.from, "d 'de' MMMM", { locale: es })} al ${format(dateRange.to, "d 'de' MMMM yyyy", { locale: es })}`
            )}
          </p>
        )}
      </div>
    </Card>
  );
}
